import KCFLogoSvg from 'assets/2020KCF_blue.svg';
import AmericanManufacturing from 'assets/american-manufacturing.jpg';
import SDLogo from 'assets/SDlogo_2blue.svg';
import Summit from 'assets/summit2022.png';
import TermsOfUse from 'assets/TermsOfUse.pdf';
import { KCFStylesProvider } from 'contexts/ThemeContext';
import { MediaQueries } from 'custom_hooks/useWindowSize';
import { ReactNode } from 'react';
import { ExternalLink } from 'routing/ExternalLink';
import styled from 'styled-components';
import { backgroundColor, primaryBoxShadow } from 'styles/theme';

const LoginPage = styled.div`
    background-image: url('${AmericanManufacturing}');
    background-size: cover;
    height: 100vh;
`;

const Column = styled.div`
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;
    align-content: center;

    @media (min-width: 320px) {
        margin-left: 1.5rem;
        margin-right: 1.5rem;
    }
`;

const LoginScreenContainer = styled.div`
    box-sizing: border-box;
    max-width: 65rem;
    width: 100%;
    padding: 2rem;
    margin-top: 5rem;

    background: ${backgroundColor};
    border-radius: 2px;
    box-shadow: ${primaryBoxShadow};

    @media (min-width: 320px) {
        margin-left: 1rem;
        margin-right: 1rem;
    }

    @media ${MediaQueries.smallAndUp} {
        padding: 4rem;
        margin-top: 10rem;
    }
`;

const LogoContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 2fr;
    grid-template-rows: auto;
    grid-column-gap: 1rem;
    align-items: center;
`;

const SummitContainer = styled.div`
    max-width: 65rem;
    width: 100%;
    margin-top: 2rem;

    @media (min-width: 320px) {
        margin-left: 1rem;
        margin-right: 1rem;
    }
    @media ${MediaQueries.smallAndUp} {
        margin-top: 2rem;
    }
`;

const Logo = styled.img`
    width: auto;
`;

const KCFLogo = styled(Logo)`
    max-height: 70px;
`;

const SummitBanner = styled(Logo)`
    max-width: 100%;
    max-height: 100%;
`;

export const TermsOfUseScreen = styled.div`
    box-sizing: border-box;
    max-width: 65rem;
    width: 100%;
    padding: 2rem;
    margin-top: 2rem;
    background: ${backgroundColor};
    border-radius: 2px;
    box-shadow: ${primaryBoxShadow};
    @media (min-width: 320px) {
        margin-left: 1rem;
        margin-right: 1rem;
    }
    @media ${MediaQueries.smallAndUp} {
        padding: 2rem;
        margin-top: 2rem;
    }
`;

export type LoginScreenProps = Readonly<{
    children: ReactNode;
}>;

export function LoginScreen({ children }: LoginScreenProps) {
    const currentDate = new Date();
    /**
     * One day after the summit end date.
     * In 2022, June 8 was the last day of the summit.
     */
    const summitEndDate = new Date('2022-06-09');
    const eulaEndDate = new Date('2023-03-30');

    return (
        <KCFStylesProvider>
            <LoginPage>
                <Column>
                    <LoginScreenContainer>
                        <LogoContainer className="mb-40">
                            <KCFLogo src={KCFLogoSvg} alt="KCF Technologies" />
                            <Logo src={SDLogo} alt="SMARTdiagnostics" />
                        </LogoContainer>
                        {children}
                    </LoginScreenContainer>

                    {/* Only show the banner if the end date has not passed.
                    For the next summit: change the URL, banner image and
                    end date (one day after the summit ends) */}
                    {currentDate < summitEndDate && (
                        <SummitContainer>
                            <ExternalLink url="https://content.kcftech.com/kcf-summit-2022">
                                <SummitBanner src={Summit} alt="KCF Summit 2022" />
                            </ExternalLink>
                        </SummitContainer>
                    )}
                    {currentDate < eulaEndDate && (
                        <TermsOfUseScreen>
                            We have updated our
                            <span>
                                <ExternalLink url={TermsOfUse} className="ml-5">
                                    Terms of Use
                                </ExternalLink>
                            </span>
                        </TermsOfUseScreen>
                    )}
                </Column>
            </LoginPage>
        </KCFStylesProvider>
    );
}
